'use client'

/**
 * SmartTelegramButton — клиентская обёртка над «Написать в Telegram».
 *
 * Заменяет hardcoded `<a href="https://t.me/Eva_iibot">` в промо-футере,
 * настройках поддержки и странице контактов. Поведение по клику:
 *
 *   - Авторизованный пользователь: POST `/api/telegram/start-link` → получает
 *     `t.me/Eva_iiBot?start=link_TOKEN` → открываем в новой вкладке. Бот
 *     сразу понимает, кто пришёл, и привязывает аккаунт.
 *   - Гость (401): открываем `t.me/Eva_iiBot` без токена.
 *   - Сетевая ошибка: bare URL + toast.error.
 *
 * XSS: дети рендерятся через React (auto-escape). `dangerouslySetInnerHTML`
 * не используется. `window.open(..., 'noopener,noreferrer')` исключает
 * window.opener-атаку.
 *
 * Race-condition: `disabled` state на время fetch защищает от двойного клика.
 */

import { useCallback, useState } from 'react'
import type { ReactNode, MouseEvent } from 'react'
import { toast } from 'sonner'

const BARE_URL = 'https://t.me/Eva_iiBot'

export interface SmartTelegramButtonProps {
  children?: ReactNode
  className?: string
  'aria-label'?: string
}

function openInNewTab(url: string): void {
  // noopener,noreferrer — защита от window.opener-атак.
  window.open(url, '_blank', 'noopener,noreferrer')
}

export function SmartTelegramButton({
  children,
  className,
  'aria-label': ariaLabel,
}: SmartTelegramButtonProps) {
  const [busy, setBusy] = useState(false)

  const handleClick = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      if (busy) return
      setBusy(true)
      try {
        const res = await fetch('/api/telegram/start-link', { method: 'POST' })
        if (res.ok) {
          const data = (await res.json()) as { url?: string }
          if (data?.url) {
            openInNewTab(data.url)
          } else {
            openInNewTab(BARE_URL)
          }
          return
        }
        // Гость — 401 — открываем bare URL без POST-токена.
        if (res.status === 401) {
          openInNewTab(BARE_URL)
          return
        }
        // Серверная ошибка (5xx) — сообщаем пользователю + bare URL.
        if (res.status >= 500) {
          toast.error('Не удалось подготовить ссылку, открываем бот')
          openInNewTab(BARE_URL)
          return
        }
        // Прочие коды (4xx кроме 401) — silent fallback на bare URL.
        openInNewTab(BARE_URL)
      } catch {
        toast.error('Не удалось подготовить ссылку, открываем бот')
        openInNewTab(BARE_URL)
      } finally {
        setBusy(false)
      }
    },
    [busy],
  )

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className={className}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  )
}

export default SmartTelegramButton
